import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { trpc } from "@/utils/trpc";
import { motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  FlaskConical,
  Landmark,
  Loader2,
  Lock,
  Smartphone,
} from "lucide-react";
import { useConnectDispatch } from "../ConnectContext";
import BridgeIcon from "../components/BridgeIcon";
import ConnectStep from "../components/ConnectStep";
import { AccountSelectionMode, ActionType } from "../types";

const ConsentItem = ({
  icon: Icon,
  title,
  description,
  className,
  delay = 0,
}: {
  icon: React.ElementType;
  title: string;
  description?: string | React.ReactNode;
  className?: string;
  delay?: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
    className={`flex flex-row gap-3 ${className}`}
  >
    <div className={`flex-shrink-0 ${description ? "mt-0.5" : ""}`}>
      <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
    </div>
    <div className="text-sm">
      <div className="font-medium text-foreground">{title}</div>
      {description && (
        <div className="text-muted-foreground mt-0.5 leading-relaxed">
          {description}
        </div>
      )}
    </div>
  </motion.div>
);

const InitStep = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const applicationId = urlParams.get("applicationId");
  const connectionId = urlParams.get("connectionId");
  const externalUserId = urlParams.get("externalUserId");
  const accountSelectionModeParam = urlParams.get("accountSelectionMode");
  const accountSelectionMode: AccountSelectionMode | undefined =
    accountSelectionModeParam === "single" ||
    accountSelectionModeParam === "multi" ||
    accountSelectionModeParam === "all"
      ? accountSelectionModeParam
      : undefined;
  const isReconnect = !!connectionId;

  const appConfig = trpc.connect.getApplicationConfig.useQuery(
    { applicationId: applicationId as string },
    {
      enabled: !!applicationId,
    },
  );
  const createSessionMutation = trpc.connect.createSession.useMutation();
  const dispatch = useConnectDispatch();

  // Check if connection limit is reached (only for new connections, not reconnects)
  const connectionLimitReached =
    !isReconnect && appConfig.data?.connectionLimitReached === true;

  const handleClickNext = async () => {
    if (!applicationId) {
      console.warn("No applicationId provided");
      return;
    }

    if (!appConfig.data) {
      console.warn("Application config not loaded");
      return;
    }

    if (connectionLimitReached) {
      console.warn("Connection limit reached");
      return;
    }

    const result = await createSessionMutation.mutateAsync({
      applicationId,
      connectionId: connectionId || undefined,
    });

    if (result.isReconnect && result.institution) {
      dispatch({
        type: ActionType.InitializeReconnectSession,
        payload: {
          sessionId: result.connectSessionId,
          connectionId: result.connectionId!,
          appConfig: appConfig.data!,
          accountSelectionMode,
          institution: {
            id: result.institution!.id,
            name: result.institution!.name,
            domain: result.institution!.domain,
            iconUrl: result.institution?.iconUrl || undefined,
            authFields: result.institution!.authFields,
            primaryColor: result.institution?.primaryColor,
            primaryForeground: result.institution?.primaryForeground,
            accentColor: result.institution?.accentColor,
            surfaceColor: result.institution?.surfaceColor,
          },
        },
      });
    } else {
      dispatch({
        type: ActionType.InitializeSession,
        payload: {
          applicationId,
          externalUserId: externalUserId || undefined,
          appConfig: appConfig.data!,
          sessionId: result.connectSessionId,
          accountSelectionMode,
        },
      });
    }
  };

  const isInitializing = appConfig.isLoading || appConfig.isError;

  return (
    <ConnectStep
      stepId="init"
      nextButtonLabel={
        !isInitializing && !connectionLimitReached ? "Continuar" : undefined
      }
      nextLoading={createSessionMutation.isLoading}
      onClickNext={handleClickNext}
      footer={
        !isInitializing &&
        !connectionLimitReached && (
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            Al continuar, aceptas los{" "}
            <a
              href="https://bridge.com.do/terminos-uso-bridge-connect"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground transition-colors"
            >
              Términos de Uso
            </a>{" "}
            y la{" "}
            <a
              href="https://bridge.com.do/politica-de-privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground transition-colors"
            >
              Política de Privacidad
            </a>{" "}
            de Bridge.
          </p>
        )
      }
    >
      {isInitializing && (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
        </div>
      )}

      {!isInitializing && (
        <div className="h-full flex flex-col">
          {/* Connection visualization */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="flex flex-row items-center justify-center pt-2 pb-6"
          >
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center shadow-sm border border-border/50"
            >
              <Smartphone className="h-5 w-5 text-muted-foreground" />
            </motion.div>

            {/* Left connector with traveling dot (app -> Bridge) */}
            <div className="relative w-7 h-[2px] bg-border mx-1">
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#B4F132]"
                initial={{ left: "-10%", opacity: 0 }}
                animate={{ left: "100%", opacity: [0, 1, 1, 0] }}
                transition={{
                  duration: 1,
                  delay: 1,
                  repeat: Infinity,
                  repeatDelay: 5,
                  ease: "easeInOut",
                }}
              />
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
              className="w-14 h-14 rounded-2xl bg-[#B4F132] flex items-center justify-center shadow-md overflow-hidden"
            >
              <BridgeIcon width={56} height={56} />
            </motion.div>

            {/* Right connector with traveling dot (Bridge -> bank) */}
            <div className="relative w-7 h-[2px] bg-border mx-1">
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#B4F132]"
                initial={{ left: "-10%", opacity: 0 }}
                animate={{ left: "100%", opacity: [0, 1, 1, 0] }}
                transition={{
                  duration: 1,
                  delay: 1.5,
                  repeat: Infinity,
                  repeatDelay: 5,
                  ease: "easeInOut",
                }}
              />
            </div>

            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center shadow-sm border border-border/50"
            >
              <Landmark className="h-5 w-5 text-muted-foreground" />
            </motion.div>
          </motion.div>

          {/* Title */}
          {isReconnect ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center space-y-1.5 mb-6"
            >
              <h1 className="text-xl font-semibold tracking-tight">
                Reconectar cuenta bancaria
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Tus credenciales han expirado. Por favor, vuelve a iniciar
                sesión para restaurar la conexión.
              </p>
            </motion.div>
          ) : (
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-semibold text-center mb-6 tracking-tight leading-snug"
            >
              <span className="text-foreground">{appConfig.data.name}</span>{" "}
              <span className="text-muted-foreground font-normal">utiliza</span>{" "}
              <span className="text-foreground">Bridge</span>{" "}
              <span className="text-muted-foreground font-normal">
                para conectar tus cuentas bancarias
              </span>
            </motion.h1>
          )}

          {connectionLimitReached ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Límite alcanzado</AlertTitle>
              <AlertDescription>
                Esta aplicación ha alcanzado el límite máximo de conexiones
                permitidas. Por favor, contacta al desarrollador de la
                aplicación para más información.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-4 flex-1">
              <ConsentItem
                icon={CheckCircle2}
                title="Simple y rápido"
                description="Conecta tus cuentas en segundos."
                delay={0.4}
              />

              <ConsentItem
                icon={Lock}
                title="Tus datos son tuyos"
                description={
                  <>
                    Bridge no vende tus datos personales y solo compartirá los
                    datos solicitados a esta aplicación ({appConfig.data.name}).
                  </>
                }
                delay={0.5}
              />

              {appConfig.data.isSandbox && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-auto"
                >
                  <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 p-3 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
                    <FlaskConical className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      Utilizando modo de prueba
                    </span>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      )}
    </ConnectStep>
  );
};

export default InitStep;
